/**
 * シフトの休憩の位置（日ビューのバーに縞で描く。10/7）
 *
 * 返す値は、シフトの開始からの分（[開始, 終了]）。次の順で決める。
 * 1. 保存済みで、明細に休憩がある → その明細
 * 2. パターンのシフトで、時刻がパターンどおり → パターンの休憩の明細（保存直後で、盤面を読み直す前のシフト）
 * 3. 休憩の分数がある → シフトの中央（5 分単位に切り下げ。サーバの splitWithBreak と同じ式）。自動休憩の目安もここで描く
 */
import {
  BusinessInfo, PatternInfo, ShiftItem, SimulationShift,
} from './types';
import { hmToMinutes } from './time';

export type BreakSegment = [number, number];

const offsetOf = (shiftStart: number, hm: string, nextDay: boolean): number => {
  let m = hmToMinutes(hm, nextDay) - shiftStart;
  // 翌日の明細で、翌日の印が無いもの（日付をまたぐシフト）
  if (m < 0) {
    m += 1440;
  }
  return m;
};

export const breakSegments = (
  s: SimulationShift, saved: ShiftItem | undefined, pattern: PatternInfo | undefined, businesses: Array<BusinessInfo>,
): Array<BreakSegment> => {
  if (s.attendType !== 0 || !s.start || !s.end) {
    return [];
  }
  const start = hmToMinutes(s.start);
  // 1. 保存済みの明細（時刻を変えていないときだけ）
  if (saved && saved.start === s.start && saved.end === s.end && saved.details.length > 0) {
    const fromSaved = saved.details
      .filter((d) => d.isBreak)
      .map((d): BreakSegment => [offsetOf(start, d.start, d.isStartNextDay), offsetOf(start, d.end, d.isEndNextDay)]);
    if (fromSaved.length > 0) {
      return fromSaved;
    }
  }
  // 2. パターンの明細（時刻がパターンどおりのときだけ。違えばサーバは明細 1 本で作る）
  if (pattern && pattern.start === s.start && pattern.end === s.end) {
    const breakIds: { [id: string]: boolean } = {};
    businesses.forEach((b) => {
      if (b.isBreak) {
        breakIds[b.businessId] = true;
      }
    });
    const fromPattern = pattern.details
      .filter((d) => d.businessId && breakIds[d.businessId])
      .map((d): BreakSegment => [offsetOf(start, d.start, d.isStartNextDay), offsetOf(start, d.end, d.isEndNextDay)]);
    if (fromPattern.length > 0) {
      return fromPattern;
    }
  }
  // 3. 休憩の分数から、中央に置く
  const span = hmToMinutes(s.end, s.isEndNextDay) - start + (hmToMinutes(s.end, s.isEndNextDay) <= start ? 1440 : 0);
  const brk = s.breakMinutes || 0;
  if (brk <= 0 || brk >= span) {
    return [];
  }
  const before = Math.floor((span - brk) / 2 / 5) * 5;
  return [[before, before + brk]];
};
